// src/components/common/search/SearchDateRange.tsx

import React from 'react';
import { Grid, Paper, Typography } from '@mui/material';

import SearchTextField from './SearchTextField';

export interface SearchDateRangeProps {
  /** Section label displayed above the date inputs. */
  label: string;

  /**
   * Controlled values (YYYY-MM-DD).
   * Empty/undefined values are rendered as an empty string.
   */
  from?: string;
  to?: string;

  /**
   * Change handlers.
   * This component normalizes empty input to `null` for consumers.
   */
  onChangeFrom: (value: string | null) => void;
  onChangeTo: (value: string | null) => void;

  disabled?: boolean;
}

/**
 * SearchDateRange
 *
 * A simple "From / To" date range input for search filters.
 * - Uses native date inputs (`type="date"`)
 * - Keeps both inputs on the same row to avoid height/layout mismatches
 */
const SearchDateRange: React.FC<SearchDateRangeProps> = ({
  label,
  from,
  to,
  onChangeFrom,
  onChangeTo,
  disabled = false,
}) => {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography sx={{ fontWeight: 600, mb: 1 }}>{label}</Typography>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <SearchTextField
            label="From"
            type="date"
            size="small"
            fullWidth
            disabled={disabled}
            value={from ?? ''}
            InputLabelProps={{ shrink: true }}
            onChangeValue={(v) => onChangeFrom(v || null)}
            onClear={() => onChangeFrom(null)}
          />
        </Grid>

        <Grid item xs={6}>
          <SearchTextField
            label="To"
            type="date"
            size="small"
            fullWidth
            disabled={disabled}
            value={to ?? ''}
            InputLabelProps={{ shrink: true }}
            onChangeValue={(v) => onChangeTo(v || null)}
            onClear={() => onChangeTo(null)}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SearchDateRange;
